import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Star, ThumbsUp, ThumbsDown, Meh } from 'lucide-react';
import { toast } from 'sonner';

interface TourFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TourFeedbackModal({ isOpen, onClose }: TourFeedbackModalProps) {
  const [rating, setRating] = useState<string>('');
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!rating) {
      toast.error('Please select a rating');
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // In a real app, you would send this to your backend
    console.log('Tour Feedback:', { rating, feedback, timestamp: new Date().toISOString() });
    
    toast.success('Thank you for your feedback!');
    setIsSubmitting(false);
    onClose();
    
    // Reset form
    setRating('');
    setFeedback('');
  };

  const handleSkip = () => {
    onClose();
    setRating('');
    setFeedback('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleSkip}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-primary" />
            How was the tour?
          </DialogTitle>
          <DialogDescription>
            Your feedback helps us improve the onboarding experience
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-3">
            <Label>How helpful was the tour?</Label>
            <RadioGroup value={rating} onValueChange={setRating}>
              <div className="flex items-center space-x-2 p-3 rounded-lg border hover:bg-accent transition-colors cursor-pointer">
                <RadioGroupItem value="very-helpful" id="very-helpful" />
                <Label htmlFor="very-helpful" className="flex items-center gap-2 cursor-pointer flex-1">
                  <ThumbsUp className="h-4 w-4 text-green-600" />
                  Very helpful
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-3 rounded-lg border hover:bg-accent transition-colors cursor-pointer">
                <RadioGroupItem value="somewhat-helpful" id="somewhat-helpful" />
                <Label htmlFor="somewhat-helpful" className="flex items-center gap-2 cursor-pointer flex-1">
                  <Meh className="h-4 w-4 text-yellow-600" />
                  Somewhat helpful
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-3 rounded-lg border hover:bg-accent transition-colors cursor-pointer">
                <RadioGroupItem value="not-helpful" id="not-helpful" />
                <Label htmlFor="not-helpful" className="flex items-center gap-2 cursor-pointer flex-1">
                  <ThumbsDown className="h-4 w-4 text-red-600" />
                  Not helpful
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback">Additional feedback (optional)</Label>
            <Textarea
              id="feedback"
              placeholder="Tell us what you liked or what could be improved..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter className="flex-row gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={handleSkip}
            className="flex-1"
            disabled={isSubmitting}
          >
            Skip
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
